import React, { useState, useEffect} from 'react';
import { Badge, Button, Modal } from 'react-bootstrap';
import FormGenerator from '../../../../../form-generator/FormGenerator';
import HorizontalProgress from '../../../../common/HorizontalProgress';
import { addLiabilityForm, updateLiabilityForm } from './form';
import ExplainatoryVideoButton from '../../../../common/ExplainatoryVideoButton';
import LeverageOptionsModal from './LeverageOptionsModal';
import UnderlineButton from '../../../../common/UnderlineButton';
import { InfoCircle } from 'react-bootstrap-icons';
import useLocalization from '../../../../../hooks/useLocalization';
import CollapsibleSection from '../../../../common/CollapsibleSection';
import OneOffEventSheet from './OneOffEventSheet';

const LiabilityModal = ({
  show,
  toBeEditedLiability,
  onHide,
  showProgress,
  strategyYears,
  linkedAsset,
  fromAssetCreateForm,
  assetOptions = [],
  cashAssetOptions = [],
  loanType,
  loanStartDateAutoCalculated,
  loanEndDateAutoCalculated,
  onLiabilitySubmit,
  onGroupLiabilitySubmit,
  liabilityStartYear,
  liabilityEndYear,
  incompleteYearsOfStrategy,
  loanEditMode,
  compTable,
  strategy,
  calculatedStrategy,
  onLeverageOptionSelect,
  onEditOneOffEvent,
  onDeleteOneOffEvent,
  onAddOneOffEvent,
  readOnlyMode = false
}) => {
  const [showLeverageOptions, setShowLeverageOptions] = useState(false);
  const [oneOffCollapsed, setOneOffCollapsed] = useState(true);
  
  useEffect(() => {
    if (show) {
      setOneOffCollapsed(true);
    }
  }, [show]);
  const { translate } = useLocalization();
  const onViewLeverageOptionClick = () => {
    setShowLeverageOptions(true);
  };

  const onLeverageOptionSelectClick = (option, startMonth, startYear) => {
    onLeverageOptionSelect(option, startMonth, startYear);
    setShowLeverageOptions(false);
  };

  return (
    <>
      <Modal size="lg" show={show} onHide={onHide} centered backdrop="static">
        <Modal.Header closeButton={!showProgress}>
          <Modal.Title className="flex-grow-1">
            <h6 className="mb-0">
              {toBeEditedLiability ? translate('update_liability') : translate('add_new_liability')}
            </h6>
          </Modal.Title>
          <UnderlineButton
            className="mx-2"
            iconSize={15}
            variant="dark"
            Icon={InfoCircle}
            fontSize="large"
            text={translate('view_leverage_options')}
            onClick={onViewLeverageOptionClick}
          />
        </Modal.Header>
        <Modal.Body className="overflow-auto">
          <div className="px-3">
            <ExplainatoryVideoButton videoKey="add_liability" />
          </div>
          {/* From asset create form then do not allow selecting other assets */}
          {fromAssetCreateForm && linkedAsset && (
            <div className="mt-3 px-2 text-dark">
              <h6 className="large">
                Linked Asset:{' '}
                <Badge bg="dark">
                  {linkedAsset.name} ( {linkedAsset.value} )
                </Badge>
              </h6>
              <hr className="my-1" />
            </div>
          )}
          {show && (
            <FormGenerator
              prefix="liability"
              formJson={
                toBeEditedLiability
                  ? updateLiabilityForm(
                      strategyYears,
                      liabilityStartYear,
                      liabilityEndYear,
                      incompleteYearsOfStrategy,
                      linkedAsset,
                      fromAssetCreateForm,
                      assetOptions,
                      cashAssetOptions,
                      loanType,
                      loanStartDateAutoCalculated,
                      loanEndDateAutoCalculated,
                      loanEditMode
                    )
                  : addLiabilityForm(
                      strategyYears,
                      liabilityStartYear,
                      liabilityEndYear,
                      incompleteYearsOfStrategy,
                      linkedAsset,
                      fromAssetCreateForm,
                      assetOptions,
                      cashAssetOptions,
                      loanType,
                      loanStartDateAutoCalculated,
                      loanEndDateAutoCalculated,
                      loanEditMode
                    )
              }
              formValues={
                toBeEditedLiability ? { 'Update Liability': { ...toBeEditedLiability } } : { 'New Liability': {} }
              }
            />
          )}
          {toBeEditedLiability && onEditOneOffEvent && onDeleteOneOffEvent && (
            <div className="px-3">
              <CollapsibleSection
                key={toBeEditedLiability._id}
                sectionName="one_time_events"
                isCollapsed={oneOffCollapsed}
                onShowHideClick={() => setOneOffCollapsed(prev => !prev)}
              >
                <div className="px-2 pb-2">
                  <OneOffEventSheet
                    strategy={strategy}
                    onEditEventClick={onEditOneOffEvent}
                    onOneOffEventDeleteClick={onDeleteOneOffEvent}
                    onAddEventClick={
                      onAddOneOffEvent ? () => onAddOneOffEvent(toBeEditedLiability) : undefined
                    }
                    editable={!readOnlyMode}
                    filterByInstrument={{ refId: toBeEditedLiability._id, type: 'Liability' }}
                  />
                </div>
              </CollapsibleSection>
            </div>
          )}
          {showProgress && (
            <HorizontalProgress
              text={toBeEditedLiability ? translate('updating_liability') : translate('adding_liability')}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            disabled={showProgress}
            size="sm"
            variant="success"
            className="mr-2 text-white"
            onClick={onLiabilitySubmit}
          >
            {toBeEditedLiability ? translate('update_liability') : translate('add_liability')}
          </Button>

          <Button
            disabled={showProgress}
            size="sm"
            variant="success"
            className="mr-2 text-white"
            onClick={onGroupLiabilitySubmit}
          >
            {toBeEditedLiability ? translate('group_update_liability') : translate('group_add_liability')}
          </Button>
        </Modal.Footer>
      </Modal>
      {show && (
        <LeverageOptionsModal
          show={showLeverageOptions}
          onHide={() => setShowLeverageOptions(false)}
          strategyYears={strategyYears}
          incompleteYearsOfStrategy={incompleteYearsOfStrategy}
          assetOptions={assetOptions}
          compTable={compTable}
          calculatedStrategy={calculatedStrategy}
          onOptionSelect={onLeverageOptionSelectClick}
        />
      )}
    </>
  );
};

export default LiabilityModal;
